import { type ReactNode, useEffect, useRef, useState } from 'react';
import { GLOBAL_NAVIGATION_HEIGHT, SIDE_PANEL_Z_INDEX } from '@/shared/lib/constants';
import { cn } from '@/shared/lib/utils';
import {
  SIDE_PANEL_TRANSITION_CSS,
  SIDE_PANEL_TRANSITION_DURATION,
} from '@/shared/stores/side-panel-store';

const CONTENT_FADE_IN_DELAY = SIDE_PANEL_TRANSITION_DURATION - 100; // パネル開閉アニメーション完了を待つための遅延

// 共通のベースProps
type CommonSidePanelProps = {
  side?: 'left' | 'right';
  position?: 'fixed' | 'sticky';
  defaultWidth?: number;
  /** リサイズ時の最小幅（指定しない場合はdefaultWidthが使用される） */
  minWidth?: number;
  keepChildrenMounted?: boolean;
  enableContentFade?: boolean;
  zIndex?: number;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
};

// リサイズ可能な場合のProps
type ResizableSidePanelProps = CommonSidePanelProps & {
  enableResize: true;
  /** リサイズ時の最大幅 */
  maxWidth?: number;
  /** 幅変更時のコールバック */
  onWidthChange?: (width: number) => void;
};

// リサイズ不可能な場合のProps
type NonResizableSidePanelProps = CommonSidePanelProps & {
  enableResize?: false;
  maxWidth?: never;
  onWidthChange?: never;
};

// リサイズとポジションの組み合わせ
type BaseSidePanelProps = ResizableSidePanelProps | NonResizableSidePanelProps;

/**
 * パネルが開いている状態のProps
 * collapsedWidth は使用不可（never）
 */
type SidePanelOpenProps = BaseSidePanelProps & {
  isOpen: true;
  collapsedWidth?: never;
};

/**
 * パネルが閉じている（折りたたまれている）状態のProps
 * collapsedWidth で折りたたみ時の幅を指定可能
 */
type SidePanelCollapsedProps = BaseSidePanelProps & {
  isOpen: false;
  collapsedWidth?: number;
};

/**
 * パネルの開閉状態が動的な場合のProps
 * collapsedWidth を指定可能
 */
type SidePanelDynamicProps = BaseSidePanelProps & {
  isOpen: boolean;
  collapsedWidth?: number;
};

type SidePanelProps = SidePanelOpenProps | SidePanelCollapsedProps | SidePanelDynamicProps;

export const SidePanel = ({
  isOpen,
  side = 'right',
  defaultWidth = 320,
  minWidth,
  maxWidth = 960,
  collapsedWidth = 0,
  enableResize = true,
  keepChildrenMounted = false,
  enableContentFade = true,
  position = 'fixed',
  zIndex = SIDE_PANEL_Z_INDEX,
  children,
  className,
  contentClassName,
  onWidthChange,
}: SidePanelProps) => {
  // minWidthが指定されていない場合はdefaultWidthを使用
  const effectiveMinWidth = minWidth ?? defaultWidth;
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const panelRef = useRef<HTMLElement>(null);
  const collapsed = Math.max(0, collapsedWidth);
  // 画面幅とパネル幅の短い方を採用
  const isOverlapping = width > windowWidth;
  const constrainedWidth = Math.min(width, windowWidth);
  const effectiveWidth = isOpen ? constrainedWidth : collapsed;
  const shouldRenderContent = isOpen || keepChildrenMounted;

  // ウィンドウリサイズ監視
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // コンテンツ表示アニメーション（パネルが開いた時のみ）
  useEffect(() => {
    if (!enableContentFade) return;
    if (isOpen) {
      // パネルが開く時: 少し遅延してからコンテンツを表示
      setShowContent(false);
      const timer = setTimeout(() => {
        setShowContent(true);
      }, CONTENT_FADE_IN_DELAY);
      return () => clearTimeout(timer);
    }
    // パネルが閉じる時: 即座に非表示（パネル自体が閉じるので見えない）
    setShowContent(false);
  }, [isOpen, enableContentFade]);

  // リサイズ開始
  const handleMouseDown = () => {
    if (!enableResize) return;
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // リサイズ処理
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      const newWidth = side === 'left' ? e.clientX - rect.left : rect.right - e.clientX;

      const clampedWidth = Math.max(effectiveMinWidth, Math.min(maxWidth, newWidth));

      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, side, effectiveMinWidth, maxWidth]);

  useEffect(() => {
    onWidthChange?.(effectiveWidth);
  }, [effectiveWidth, onWidthChange]);

  const borderClass = side === 'left' ? 'border-r' : 'border-l';
  const resizeHandleClass = side === 'left' ? 'right-0' : 'left-0';
  const positionClass = position === 'fixed' ? 'fixed' : 'sticky';
  const contentWrapperClass = cn('flex h-full flex-col', contentClassName);
  const content =
    enableContentFade && shouldRenderContent ? (
      <div
        className={cn('transition-opacity duration-200', showContent ? 'opacity-100' : 'opacity-0')}
      >
        {children}
      </div>
    ) : (
      children
    );

  return (
    <aside
      ref={panelRef}
      data-side-panel={side}
      style={{
        width: `${effectiveWidth}px`,
        minWidth: `${effectiveWidth}px`,
        flexShrink: 0,
        transition:
          isResizing || isOverlapping
            ? 'none'
            : `width ${SIDE_PANEL_TRANSITION_CSS}, min-width ${SIDE_PANEL_TRANSITION_CSS}`,
        top: `${GLOBAL_NAVIGATION_HEIGHT}px`,
        zIndex,
        height: `calc(100vh - ${GLOBAL_NAVIGATION_HEIGHT}px)`,
        [side === 'left' ? 'left' : 'right']: 0,
      }}
      className={cn(
        positionClass,
        'flex flex-col overflow-hidden',
        'bg-white shadow-lg',
        isOpen ? ['border-stone-200', borderClass] : 'border-0',
        className,
      )}
    >
      {shouldRenderContent && (
        <>
          {enableResize && isOpen && (
            <button
              type="button"
              aria-label="パネルサイズ変更"
              tabIndex={0}
              onMouseDown={handleMouseDown}
              className={cn(
                'absolute top-0 z-10 h-full w-1',
                'cursor-col-resize transition-colors',
                'hover:bg-blue-500',
                isResizing && 'bg-blue-500',
                resizeHandleClass,
              )}
            />
          )}
          <div className={contentWrapperClass}>
            <div className="flex-1 overflow-auto">{content}</div>
          </div>
        </>
      )}
    </aside>
  );
};
